/**
 * Resolve Songbird base URLs for client and server callers.
 *
 * Prefer these helpers over same-origin /api/stocks/* proxies so the browser
 * talks directly to Songbird (with fallback handled by songbird-fallback plugins).
 */

import { useRuntimeConfig } from "#app";

function ensureTrailingSlash(url: string): string {
	if (!url) return "";
	return url.endsWith("/") ? url : `${url}/`;
}

/** Songbird REST API prefix, e.g. `https://songbird.example.com/api/` */
export function getSongbirdApiBase(
	config: ReturnType<typeof useRuntimeConfig> = useRuntimeConfig()
): string {
	if (import.meta.server) {
		return ensureTrailingSlash((config.songbirdUrl as string) || "") + "api/";
	}

	const proxy = config.public.songbirdProxy as string | undefined;
	if (proxy) return proxy;

	const backend = (config.public.backend_url as string) || "";
	return ensureTrailingSlash(backend) + "api/";
}

/** Songbird WebSocket origin, e.g. `wss://songbird.example.com/` */
export function getSongbirdWsBase(
	config: ReturnType<typeof useRuntimeConfig> = useRuntimeConfig()
): string {
	const httpBase = import.meta.server
		? ((config.songbirdUrl as string) || "")
		: ((config.public.backend_url as string) || "");

	return ensureTrailingSlash(httpBase).replace(/^http/, "ws");
}

/** Direct Songbird OG image serve URL for a ticker.
 *
 * Use this to *trigger* generation/refresh of the TS-branded image (it serves
 * bytes and generates on-demand if missing). For the `og:image` meta tag prefer
 * `getTsOgImageS3Url`, which points crawlers straight at the public S3 object
 * (mirroring how StocksToTrade references its `stt-og-images` bucket). */
export function getSongbirdOgImageUrl(
	symbol: string,
	config: ReturnType<typeof useRuntimeConfig> = useRuntimeConfig()
): string {
	const apiBase = getSongbirdApiBase(config);
	const normalized = symbol.toUpperCase();
	return `${apiBase}stocks/ts-og-image?symbol=${encodeURIComponent(normalized)}&serve=1`;
}

/** Public S3 URL for a ticker's TS-branded OG image.
 *
 * Mirrors StocksToTrade, which references its images directly at
 * `stt-ts-resources.s3.us-east-1.amazonaws.com/stt-og-images/<SYMBOL>.webp`.
 * The TS equivalent lives under the `ts-og-images/` prefix. The object is kept
 * warm by Songbird (server-side generation on the quote path + the
 * `getSongbirdOgImageUrl` serve trigger on mount).
 *
 * NOTE: requires the `ts-og-images/*` prefix to be publicly readable in the
 * `stt-ts-resources` bucket policy (same grant `stt-og-images/*` already has). */
export function getTsOgImageS3Url(symbol: string): string {
	const normalized = symbol.toUpperCase();
	return `https://stt-ts-resources.s3.us-east-1.amazonaws.com/ts-og-images/${encodeURIComponent(
		normalized
	)}.webp`;
}
